import { useState, useEffect, useRef } from "react";
import { Button } from "../../components/Button.tsx";
import { Input } from "../../components/Input.tsx";
import { Checkbox } from "../../components/Checkbox.tsx";
import { Badge } from "../../components/Badge.tsx";
import { RiCloseLine, RiSearchLine, RiArrowLeftSLine, RiArrowRightSLine, RiInboxLine, RiDeleteBinLine, RiCheckLine } from "@remixicon/react";
import { toast } from "react-hot-toast";
import {
  fetchDataStudioEndpoints,
  fetchEndpointDefinition,
  queryDataStudio,
  countDataStudio,
  deleteLibraryItem,
  UserLibraryItem,
} from "../../services/ApiService.ts";
import {
  EndpointInfo,
  EndpointDefinition,
  DataStudioQuery,
  DataSource,
  Field,
  Parameter,
} from "../../types.ts";
import { parseUtcDate } from "../../utils.ts";
import {
  cleanModuleName,
  libraryItemSizeError,
  libraryItemToDataSource,
  MAX_CELLS,
  MAX_ROWS,
  WARN_ROWS,
} from "./library.ts";

interface DataLoadingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onDataLoaded: (source: DataSource) => void;
  initialEndpoint?: string; // Pre-select this endpoint when wizard opens
  initialMode?: "wizard" | "library"; // Which panel the wizard opens on
  libraryItems?: UserLibraryItem[];
  onLibraryItemDeleted?: (id: string) => void;
  administrationCode?: number;
}

type WizardStep = "endpoint" | "fields" | "filters";
type MetadataItem = Field | Parameter;

const DataLoadingWizard = ({ isOpen, onClose, onDataLoaded, initialEndpoint, initialMode = "wizard", libraryItems = [], onLibraryItemDeleted, administrationCode }: DataLoadingWizardProps) => {
  const [mode, setMode] = useState<"wizard" | "library">(initialMode);
  const [currentStep, setCurrentStep] = useState<WizardStep>("endpoint");
  const [endpoints, setEndpoints] = useState<EndpointInfo[]>([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>("");
  const [endpointDefinition, setEndpointDefinition] = useState<EndpointDefinition | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [selectedParameters, setSelectedParameters] = useState<Set<string>>(new Set());
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fieldSearchQuery, setFieldSearchQuery] = useState("");
  const [newFilterColumn, setNewFilterColumn] = useState<string>("");
  const [newFilterValue, setNewFilterValue] = useState<string>("");
  const [endpointDescriptions, setEndpointDescriptions] = useState<Record<string, string>>({});
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());
  const [isColumnDropdownOpen, setIsColumnDropdownOpen] = useState(false);
  const [columnSearchQuery, setColumnSearchQuery] = useState("");
  const [largeDataWarning, setLargeDataWarning] = useState<{
    count: number;
    blocked: false | "rows" | "cells";
  } | null>(null);
  const [dragFieldIndex, setDragFieldIndex] = useState<number | null>(null);
  const [dropFieldIndex, setDropFieldIndex] = useState<number | null>(null);
  const columnDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target as Node)) {
        setIsColumnDropdownOpen(false);
      }
    };

    if (isColumnDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isColumnDropdownOpen]);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      const loadEndpoints = async () => {
        const response = await fetchDataStudioEndpoints();
        if (response.error) {
          toast.error("Fout bij het laden van endpoints");
          return;
        }
        const endpointsList = response.data || [];
        setEndpoints(endpointsList);

        const descriptions: Record<string, string> = {};
        endpointsList.forEach((endpoint) => {
          descriptions[endpoint.id] = endpoint.description || "";
        });
        setEndpointDescriptions(descriptions);

        if (initialEndpoint) {
          setSelectedEndpoint(initialEndpoint);
          setCurrentStep("fields");
        }
      };
      loadEndpoints();
    }
  }, [isOpen, initialEndpoint, initialMode]);

  useEffect(() => {
    if (!selectedEndpoint) {
      setEndpointDefinition(null);
      setSelectedFields([]);
      setSelectedParameters(new Set());
      setParameters({});
      return;
    }

    const loadEndpointDefinition = async () => {
      setLoading(true);
      try {
        const definition = await fetchEndpointDefinition(selectedEndpoint);
        setEndpointDefinition(definition);
        setSelectedFields([]);
        const requiredParams = definition.parameters.filter((p) => p.required === "V" || p.required === "C");
        setSelectedParameters(new Set(requiredParams.map((p) => p.id)));
        setParameters({});
      } catch {
        toast.error("Fout bij het laden van endpoint definitie");
      } finally {
        setLoading(false);
      }
    };

    loadEndpointDefinition();
  }, [selectedEndpoint]);

  const cleanEndpointName = (endpointId: string): string => {
    if (!endpointId.startsWith("ds_")) return endpointId;
    const withoutDs = endpointId.substring(3);
    const prefixes = ["bi_", "fw_", "ef_", "p4f_", "rf_", "wm_", "rl_", "Rl_"];
    for (const prefix of prefixes) {
      if (withoutDs.startsWith(prefix)) {
        return withoutDs.substring(prefix.length);
      }
    }
    return withoutDs;
  };

  const getEndpointName = (endpointId: string): string => {
    return cleanEndpointName(endpointId);
  };

  const formatEndpointLabel = (cleanedId: string): string => {
    return cleanedId.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  };

  const getFieldsCommonPrefix = (fields: { id: string }[]): string => {
    if (fields.length === 0) return "";
    let prefix = fields[0].id;
    for (const field of fields.slice(1)) {
      while (!field.id.startsWith(prefix)) {
        prefix = prefix.slice(0, -1);
        if (!prefix) return "";
      }
    }
    const lastUnderscore = prefix.lastIndexOf("_");
    return lastUnderscore >= 0 ? prefix.slice(0, lastUnderscore + 1) : "";
  };

  const cleanFieldName = (fieldId: string, commonPrefix: string): string => {
    if (commonPrefix && fieldId.startsWith(commonPrefix)) {
      return fieldId.slice(commonPrefix.length);
    }
    return fieldId;
  };

  const formatFieldLabel = (cleanedField: string): string => {
    return cleanedField.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  };

  const getCleanedDescription = (description: string): string => {
    if (!description) return "";
    return description.replace(/^---[\s\S]*?---\s*/m, "").trim();
  };

  const getMetadataSearchText = (item: MetadataItem): string => {
    const optionText = (item.options || [])
      .map((option) => `${option.value} ${option.label}`)
      .join(" ");
    return [
      item.id,
      item.description,
      item.details || "",
      optionText,
      ...("children" in item && item.children ? item.children.map((child) => child.id) : []),
    ]
      .join(" ")
      .toLowerCase();
  };

  const renderMetadataHelp = (item: MetadataItem) => {
    const options = item.options || [];
    const hasOptions = options.length > 0;
    const hasDetails = Boolean(item.details);

    if (!hasOptions && !hasDetails) {
      return null;
    }

    return (
      <details className="mt-1">
        <summary className="cursor-pointer text-xs font-medium text-blue-700 select-none">
          Meer info
        </summary>
        <div className="mt-2 space-y-2">
          {hasDetails && <p className="text-xs text-gray-600">{item.details}</p>}
          {hasOptions && (
            <ul className="space-y-1 text-xs text-gray-600">
              {options.map((option) => (
                <li key={`${option.value}-${option.label}`}>
                  <span className="font-medium text-gray-800">{option.value}</span>
                  {" "}
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    );
  };

  const handleFieldToggle = (fieldId: string, isChecked: boolean) => {
    setSelectedFields((prev) => (isChecked ? [...prev, fieldId] : prev.filter((id) => id !== fieldId)));
  };

  // ponytail: native HTML5 drag & drop, so reordering works across wrapped lines. No touch support;
  // swap for a pointer-event based library if the portal ever needs to work on tablets.
  const handleFieldDrop = (targetIndex: number) => {
    setSelectedFields((prev) => {
      if (dragFieldIndex === null || dragFieldIndex === targetIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(dragFieldIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDragFieldIndex(null);
    setDropFieldIndex(null);
  };

  const handleSelectAllFields = () => {
    if (!endpointDefinition) return;
    setSelectedFields(endpointDefinition.fields.map((f) => f.id));
  };

  const handleParameterChange = (paramId: string, value: string) => {
    setParameters((prev) => ({
      ...prev,
      [paramId]: value,
    }));
  };

  const handleParameterToggle = (paramId: string, isChecked: boolean) => {
    setSelectedParameters((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(paramId);
      } else {
        next.delete(paramId);
        setParameters((p) => {
          const updated = { ...p };
          delete updated[paramId];
          return updated;
        });
      }
      return next;
    });
  };

  const selectEndpoint = (endpointId: string) => {
    setSelectedEndpoint(endpointId);
    setCurrentStep("fields");
  };

  const handleNext = async () => {
    if (currentStep === "endpoint") {
      if (!selectedEndpoint) {
        toast.error("Selecteer een endpoint");
        return;
      }
      setCurrentStep("fields");
    } else if (currentStep === "fields") {
      if (selectedFields.length === 0) {
        toast.error("Selecteer minimaal één veld");
        return;
      }
      setCurrentStep("filters");
    } else if (currentStep === "filters") {
      // Validate required parameters
      if (endpointDefinition) {
        const requiredParams = endpointDefinition.parameters.filter((p) => p.required === "V");
        const missingRequired = requiredParams.filter((p) => !parameters[p.id] || parameters[p.id].trim() === "");
        if (missingRequired.length > 0) {
          toast.error(`Verplichte parameters ontbreken: ${missingRequired.map((p) => p.id).join(", ")}`);
          return;
        }
      }
      // Check row count before fetching
      await handleCountCheck();
    }
  };

  const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => 2020 + i);

  const MONTH_OPTIONS = [
    { value: "01", label: "januari" },
    { value: "02", label: "februari" },
    { value: "03", label: "maart" },
    { value: "04", label: "april" },
    { value: "05", label: "mei" },
    { value: "06", label: "juni" },
    { value: "07", label: "juli" },
    { value: "08", label: "augustus" },
    { value: "09", label: "september" },
    { value: "10", label: "oktober" },
    { value: "11", label: "november" },
    { value: "12", label: "december" },
  ];

  const getParamInputType = (param: { id: string; type: string }): "year" | "month" | "date" | "boolean" | "text" => {
    const lastSegment = param.id.split("_").pop() || "";
    const type = (param.type || "").toLowerCase();
    if (lastSegment === "jaar") return "year";
    if (lastSegment === "maand") return "month";
    if (type === "xsd:datetime") return "date";
    if (type === "xsd:boolean") return "boolean";
    return "text";
  };

  const renderParamValueInput = (
    param: { id: string; type: string; options?: { value: string; label: string }[] },
    value: string,
    onChange: (v: string) => void,
    className?: string
  ) => {
    if (param.options && param.options.length > 0) {
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`corner-squircle w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className ?? ""}`}
        >
          <option value="">Kies waarde...</option>
          {param.options.map((option) => (
            <option key={`${option.value}-${option.label}`} value={option.value}>
              {option.label} ({option.value})
            </option>
          ))}
        </select>
      );
    }

    const kind = getParamInputType(param);
    if (kind === "year") {
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`corner-squircle w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className ?? ""}`}
        >
          <option value="">Kies jaar...</option>
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={String(y)}>{y}</option>
          ))}
        </select>
      );
    }
    if (kind === "month") {
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`corner-squircle w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className ?? ""}`}
        >
          <option value="">Kies maand...</option>
          {MONTH_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
      );
    }
    if (kind === "date") {
      const dateValue = value ? value.split("T")[0] : "";
      return (
        <input
          type="date"
          value={dateValue}
          onChange={(e) => onChange(e.target.value ? `${e.target.value}T00:00:00` : "")}
          className={`corner-squircle w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className ?? ""}`}
        />
      );
    }
    if (kind === "boolean") {
      return (
        <div className={`flex items-center gap-2 py-2 ${className ?? ""}`}>
          <input
            type="checkbox"
            checked={value === "true"}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">{value === "true" ? "Ja" : "Nee"}</span>
        </div>
      );
    }
    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Voer waarde in..."
        className={className}
      />
    );
  };

  const handleCountCheck = async () => {
    if (!endpointDefinition) return;
    setLoading(true);
    try {
      const activeParams = Object.fromEntries(
        Object.entries(parameters).filter(([, v]) => v && v.trim() !== "")
      );
      const response = await countDataStudio({
        module: endpointDefinition.module,
        parameters: activeParams,
        administration_code: administrationCode,
      });
      const count = response.data?.count;
      if (count != null && count > WARN_ROWS) {
        const blocked =
          count > MAX_ROWS ? "rows" : count * selectedFields.length > MAX_CELLS ? "cells" : false;
        setLargeDataWarning({ count, blocked });
      } else {
        await handleStartFetch();
      }
    } catch {
      // If count check fails, proceed anyway
      await handleStartFetch();
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep === "fields") {
      setCurrentStep("endpoint");
    } else if (currentStep === "filters") {
      setCurrentStep("fields");
    }
  };

  const buildSourceName = (): string => {
    if (!selectedEndpoint) return "dataset";

    const baseName = getEndpointName(selectedEndpoint);
    const activeParams = Object.fromEntries(
      Object.entries(parameters).filter(([, value]) => value && value.trim() !== "")
    );
    const paramParts = Object.entries(activeParams)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}_${value}`)
      .join("_");

    return paramParts ? `${baseName}_${paramParts}` : baseName;
  };

  const handleStartFetch = async () => {
    if (!endpointDefinition || selectedFields.length === 0 || !administrationCode) return;

    const activeParams = Object.fromEntries(
      Object.entries(parameters).filter(([, v]) => v && v.trim() !== "")
    );
    const query: DataStudioQuery = {
      module: endpointDefinition.module,
      parameters: activeParams,
      fields: selectedFields,
      administration_code: administrationCode,
      source_name: buildSourceName(),
      idempotency_key: crypto.randomUUID(),
    };

    const response = await queryDataStudio(query);
    if (response.error || !response.data) {
      toast.error(response.error ?? "Fout bij het ophalen van data");
      return;
    }

    onDataLoaded({
      endpoint_name: selectedEndpoint,
      source_name: buildSourceName(),
      selected_fields: selectedFields,
      parameters: activeParams,
      task_id: response.data.task_id,
      row_count: response.data.row_count,
      state: response.data.state,
    });
    handleClose();
  };

  const handleClose = () => {
    setCurrentStep("endpoint");
    setSelectedEndpoint("");
    setSelectedFields([]);
    setSelectedParameters(new Set());
    setParameters({});
    setSearchQuery("");
    setFieldSearchQuery("");
    setNewFilterColumn("");
    setNewFilterValue("");
    setEndpointDescriptions({});
    setExpandedDescriptions(new Set());
    setIsColumnDropdownOpen(false);
    setColumnSearchQuery("");
    setLargeDataWarning(null);
    onClose();
  };

  const getRequiredLabel = (required: string): string => {
    if (required === "V") return "Verplicht";
    if (required === "C") return "Voorwaardelijk";
    if (required === "O") return "Optioneel";
    return "Onbekend";
  };

  const getRequiredBadgeColor = (required: string): "red" | "yellow" | "zinc" => {
    if (required === "V") return "red";
    if (required === "C") return "yellow";
    return "zinc";
  };

  const fieldCommonPrefix = endpointDefinition ? getFieldsCommonPrefix(endpointDefinition.fields) : "";

  // Filter endpoints based on search
  const filteredEndpoints = endpoints.filter((endpoint) => {
    const name = cleanEndpointName(endpoint.id).toLowerCase();
    const search = searchQuery.toLowerCase();
    const description = (endpointDescriptions[endpoint.id] || "").toLowerCase();
    return name.includes(search) || description.includes(search);
  });

  const formatLibraryDate = (iso: string | null): string => {
    if (!iso) return "";
    return parseUtcDate(iso).toLocaleString("nl-NL", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const handleAddFromLibrary = (item: UserLibraryItem) => {
    const sizeError = libraryItemSizeError(item);
    if (sizeError) {
      toast.error(sizeError, { duration: 10000 });
      return;
    }
    onDataLoaded(libraryItemToDataSource(item));
    handleClose();
    toast.success("Data uit databibliotheek toegevoegd");
  };

  const handleDeleteLibraryItem = async (item: UserLibraryItem) => {
    if (!administrationCode) return;
    const res = await deleteLibraryItem(item.id, administrationCode);
    if (res.error) {
      toast.error("Kon item niet verwijderen");
      return;
    }
    onLibraryItemDeleted?.(item.id);
    toast.success("Item verwijderd uit databibliotheek");
  };

  if (!isOpen) return null;

  if (largeDataWarning) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="corner-squircle bg-white rounded-[1.5rem] shadow-xl w-full max-w-md p-6">
          {largeDataWarning.blocked ? (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Te veel gegevens om te openen</h2>
              {largeDataWarning.blocked === "cells" ? (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    Deze selectie bevat <span className="font-semibold text-gray-900">{largeDataWarning.count.toLocaleString("nl-NL")} rijen</span> met{" "}
                    <span className="font-semibold text-gray-900">{selectedFields.length} kolommen</span>. Dat is te
                    veel om in één keer te openen — het maximum is{" "}
                    {MAX_CELLS.toLocaleString("nl-NL")} velden in totaal (rijen maal kolommen).
                  </p>
                  <p className="text-sm text-gray-600 mb-6">
                    Kies minder kolommen, of maak de selectie kleiner met een kortere periode of een extra filter.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    Deze selectie bevat <span className="font-semibold text-gray-900">{largeDataWarning.count.toLocaleString("nl-NL")} rijen</span>. Dat is te
                    veel om in één keer te openen — het maximum is{" "}
                    {MAX_ROWS.toLocaleString("nl-NL")} rijen.
                  </p>
                  <p className="text-sm text-gray-600 mb-6">
                    Maak de selectie kleiner en probeer het opnieuw. Kies bijvoorbeeld een kortere periode, zoals één
                    maand in plaats van een heel jaar, of voeg een filter toe om alleen de gegevens op te halen die je
                    nodig hebt.
                  </p>
                </>
              )}
              <div className="flex justify-end">
                <Button color="primary" onClick={() => setLargeDataWarning(null)}>
                  Filters aanpassen
                </Button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Grote hoeveelheid data</h2>
              <p className="text-sm text-gray-600 mb-6">
                Je staat op het punt om <span className="font-semibold text-gray-900">{largeDataWarning.count.toLocaleString()} rijen</span> op te halen. Dit kan enige tijd duren. Wil je doorgaan?
              </p>
              <div className="flex justify-end gap-3">
                <Button plain onClick={() => setLargeDataWarning(null)}>
                  Annuleren
                </Button>
                <Button
                  color="primary"
                  onClick={async () => {
                    setLargeDataWarning(null);
                    await handleStartFetch();
                  }}
                >
                  Doorgaan
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const steps = [
    { id: "endpoint", label: "Onderwerp", number: 1 },
    { id: "fields", label: "Kolommen", number: 2 },
    { id: "filters", label: "Filters", number: 3 },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="corner-squircle bg-white rounded-[1.75rem] shadow-xl w-full max-w-4xl h-[90vh] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold">
              {mode === "library" && "Uit databibliotheek"}
              {mode === "wizard" && currentStep === "endpoint" && "Wat wil je bekijken?"}
              {mode === "wizard" && currentStep === "fields" && "Kies kolommen"}
              {mode === "wizard" && currentStep === "filters" && "Filters toevoegen"}
            </h2>
            {libraryItems.length > 0 && (
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                <button
                  onClick={() => setMode("wizard")}
                  className={`px-3 py-1.5 transition-colors ${mode === "wizard" ? "bg-blue-500 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  Nieuw laden
                </button>
                <button
                  onClick={() => setMode("library")}
                  className={`px-3 py-1.5 transition-colors flex items-center gap-1.5 ${mode === "library" ? "bg-blue-500 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  <RiInboxLine className="w-3.5 h-3.5" />
                  Databibliotheek
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${mode === "library" ? "bg-blue-400" : "bg-gray-100 text-gray-500"}`}>
                    {libraryItems.length}
                  </span>
                </button>
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RiCloseLine className="w-6 h-6" />
          </button>
        </div>

        {/* Library panel */}
        {mode === "library" && (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              {libraryItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 py-16">
                  <RiInboxLine className="w-12 h-12 mb-3 opacity-40" />
                  <p className="text-sm">Je databibliotheek is nog leeg.</p>
                  <p className="text-xs mt-1">Bewaar een geladen dataset om hem hier terug te vinden.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {libraryItems.map((item) => {
                    const name = cleanModuleName(item.module);
                    const paramEntries = Object.entries(item.parameters);
                    return (
                      <div key={item.id} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:bg-blue-50/30 transition-colors group">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 capitalize">{name.replace(/_/g, " ")}</p>
                            {paramEntries.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {paramEntries.map(([k, v]) => (
                                  <span key={k} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                    {k.split("_").pop()}: <strong>{String(v)}</strong>
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              <span>{item.fields.length} kolommen</span>
                              {item.row_count != null && <span>{item.row_count.toLocaleString()} rijen</span>}
                              {item.updated_at && <span>{formatLibraryDate(item.updated_at)}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleDeleteLibraryItem(item)}
                              className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                              title="Verwijder uit databibliotheek"
                            >
                              <RiDeleteBinLine className="w-4 h-4" />
                            </button>
                            <Button color="primary" onClick={() => handleAddFromLibrary(item)}>
                              Toevoegen
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end">
              <Button plain onClick={handleClose}>Annuleren</Button>
            </div>
          </>
        )}

        {/* Wizard: progress + content + footer */}
        {mode === "wizard" && <>
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center">
                  {index < currentStepIndex ? (
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  ) : index === currentStepIndex ? (
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">{step.number}</span>
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 text-sm font-semibold">{step.number}</span>
                    </div>
                  )}
                  <span className={`ml-2 text-sm ${index === currentStepIndex ? "font-semibold text-blue-600" : "text-gray-600"}`}>
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${index < currentStepIndex ? "bg-green-500" : "bg-gray-300"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <>
            {currentStep === "endpoint" && (
            <div className="space-y-4">
              <div className="sticky -top-6 z-10 -mt-6 bg-white pb-2 pt-6">
                <div className="relative">
                  <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Zoek een onderwerp..."
                    className="corner-squircle w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-1">
                {filteredEndpoints.map((endpoint) => {
                  const description = endpointDescriptions[endpoint.id] || "";
                  const cleanedDesc = getCleanedDescription(description);
                  const isExpanded = expandedDescriptions.has(endpoint.id);
                  const MAX_DESC_LENGTH = 125;
                  const shouldTruncate = cleanedDesc.length > MAX_DESC_LENGTH;
                  const displayDesc = isExpanded || !shouldTruncate
                    ? cleanedDesc
                    : cleanedDesc.substring(0, MAX_DESC_LENGTH) + "...";

                  const handleMeerClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setExpandedDescriptions((prev) => {
                      const next = new Set(prev);
                      if (isExpanded) {
                        next.delete(endpoint.id);
                      } else {
                        next.add(endpoint.id);
                      }
                      return next;
                    });
                  };

                  return (
                    <div
                      key={endpoint.id}
                      role="button"
                      aria-pressed={selectedEndpoint === endpoint.id}
                      tabIndex={0}
                      onClick={() => selectEndpoint(endpoint.id)}
                      onKeyDown={(e) => {
                        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                          e.preventDefault();
                          selectEndpoint(endpoint.id);
                        }
                      }}
                      className={`corner-squircle p-3 rounded-[1.2rem] cursor-pointer transition-colors ${
                        selectedEndpoint === endpoint.id
                          ? "bg-blue-50"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {selectedEndpoint === endpoint.id && <RiCheckLine className="w-4 h-4 text-blue-600 flex-shrink-0" aria-hidden="true" />}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-xs">{formatEndpointLabel(cleanEndpointName(endpoint.id))}</h3>
                          {cleanedDesc && (
                            <>
                              <p className="text-xs text-gray-600 mt-0.5">{displayDesc}</p>
                              {shouldTruncate && (
                                <button
                                  onClick={handleMeerClick}
                                  className="text-xs text-blue-600 hover:underline mt-0.5 inline-block"
                                >
                                  {isExpanded ? "minder" : "meer"}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {currentStep === "fields" && endpointDefinition && (
            <div className="flex h-full flex-col gap-4">
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase">Beschikbare kolommen</h3>
                <div className="relative">
                  <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
                  <input
                    type="text"
                    value={fieldSearchQuery}
                    onChange={(e) => setFieldSearchQuery(e.target.value)}
                    placeholder="Zoek een kolom..."
                  className="corner-squircle w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="corner-squircle flex min-h-0 flex-1 flex-col rounded-[1.2rem] border p-3">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Checkbox
                      checked={selectedFields.length === endpointDefinition.fields.length}
                      onChange={(checked) => {
                        if (checked) {
                          handleSelectAllFields();
                        } else {
                          setSelectedFields([]);
                        }
                      }}
                    />
                    <span className="text-sm font-medium">Selecteer alles</span>
                  </div>
                  <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pt-3">
                    {endpointDefinition.fields
                      .filter((field) => {
                        const name = cleanFieldName(field.id, fieldCommonPrefix).replace(/_/g, " ").toLowerCase();
                        const search = fieldSearchQuery.toLowerCase();
                        return name.includes(search) || getMetadataSearchText(field).includes(search);
                      })
                      .map((field) => {
                        const cleanName = formatFieldLabel(cleanFieldName(field.id, fieldCommonPrefix));
                        return (
                          <div key={field.id} className="flex items-start gap-2">
                            <Checkbox
                              className="mt-1.5"
                              checked={selectedFields.includes(field.id)}
                              onChange={(checked) => handleFieldToggle(field.id, checked)}
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium">{cleanName}</span>
                              {field.description && (
                                <p className="text-xs text-gray-500">{field.description}</p>
                              )}
                              {field.children && field.children.length > 0 && (
                                <p className="text-xs text-gray-500">
                                  Bevat per item:{" "}
                                  {field.children
                                    .map((child) => formatFieldLabel(cleanFieldName(child.id, fieldCommonPrefix)))
                                    .join(", ")}
                                </p>
                              )}
                              {renderMetadataHelp(field)}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex items-baseline gap-2 mb-2">
                      <h3 className="text-sm font-semibold text-gray-700 uppercase">Geselecteerde kolommen</h3>
                      {selectedFields.length > 0 && (
                        <span className="text-xs text-gray-500">Sleep om de kolomvolgorde te wijzigen</span>
                      )}
                    </div>
                    {selectedFields.length === 0 ? (
                      <p className="text-sm text-gray-500 w-full">Nog geen kolommen geselecteerd</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectedFields.map((fieldId, index) => {
                          const field = endpointDefinition.fields.find((f) => f.id === fieldId);
                          const cleanName = formatFieldLabel(cleanFieldName(field ? field.id : fieldId, fieldCommonPrefix));
                          // The caret marks the slot the badge actually lands in: after the hovered
                          // badge when dragging right, before it when dragging left.
                          const dropSide =
                            dropFieldIndex === index && dragFieldIndex !== null && dragFieldIndex !== index
                              ? dragFieldIndex < index
                                ? "-right-1"
                                : "-left-1"
                              : null;
                          return (
                            <div
                              key={fieldId}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData("text/plain", fieldId);
                                setDragFieldIndex(index);
                              }}
                              onDragEnd={() => {
                                setDragFieldIndex(null);
                                setDropFieldIndex(null);
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDropFieldIndex(index);
                              }}
                              onDrop={() => handleFieldDrop(index)}
                              className={`relative cursor-grab select-none active:cursor-grabbing ${dragFieldIndex === index ? "opacity-40" : ""}`}
                            >
                              {dropSide && (
                                <span className={`absolute top-0 bottom-0 w-0.5 rounded bg-blue-600 ${dropSide}`} />
                              )}
                              <Badge color="blue" className="flex items-center gap-1">
                                {cleanName}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFieldToggle(fieldId, false);
                                  }}
                                  className="ml-1 hover:text-red-600"
                                >
                                  ×
                                </button>
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === "filters" && endpointDefinition && (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative" ref={columnDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Kolom</label>
                    <button
                      type="button"
                      onClick={() => setIsColumnDropdownOpen(!isColumnDropdownOpen)}
                      className="corner-squircle w-full text-left px-3 py-2.5 bg-white border border-gray-200 rounded-[1.1rem] text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
                    >
                      <span>{newFilterColumn || "Kies kolom..."}</span>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${isColumnDropdownOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isColumnDropdownOpen && (
                      <div className="corner-squircle absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-[1.1rem] shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                              type="text"
                              value={columnSearchQuery}
                              onChange={(e) => setColumnSearchQuery(e.target.value)}
                              placeholder="Zoek kolom..."
                              className="corner-squircle w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {newFilterColumn && (
                            <button
                              type="button"
                              onClick={() => {
                                setNewFilterColumn("");
                                setNewFilterValue("");
                                setIsColumnDropdownOpen(false);
                                setColumnSearchQuery("");
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 border-b"
                            >
                              Kies kolom...
                            </button>
                          )}
                          {endpointDefinition.parameters
                            .filter((param) => {
                              if (selectedParameters.has(param.id)) return false;
                              const search = columnSearchQuery.toLowerCase();
                              return getMetadataSearchText(param).includes(search);
                            })
                            .map((param) => (
                              <button
                                key={param.id}
                                type="button"
                                onClick={() => {
                                  setNewFilterColumn(param.id);
                                  setIsColumnDropdownOpen(false);
                                  setColumnSearchQuery("");
                                  setNewFilterValue(getParamInputType(param) === "boolean" ? "false" : "");
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="truncate">{param.id}</div>
                                  {param.description && (
                                    <div className="truncate text-xs text-gray-500">{param.description}</div>
                                  )}
                                </div>
                                {newFilterColumn === param.id && (
                                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Waarde</label>
                    <div className="flex gap-2">
                      {(() => {
                        const paramDef = endpointDefinition.parameters.find((p) => p.id === newFilterColumn);
                        if (!paramDef) {
                          return (
                            <Input
                              type="text"
                              value=""
                              disabled
                              placeholder="Kies eerst een kolom"
                              className="flex-1"
                            />
                          );
                        }
                        return (
                          <div className="flex-1">
                            {renderParamValueInput(paramDef, newFilterValue, setNewFilterValue)}
                          </div>
                        );
                      })()}
                      <Button
                        color="primary"
                        onClick={() => {
                          if (newFilterColumn) {
                            handleParameterToggle(newFilterColumn, true);
                            handleParameterChange(newFilterColumn, newFilterValue);
                            setNewFilterColumn("");
                            setNewFilterValue("");
                          }
                        }}
                        disabled={!newFilterColumn}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">Actieve filters</h3>
                {selectedParameters.size === 0 ? (
                  <p className="text-sm text-gray-500">
                    Nog geen filters toegevoegd — je kunt ook zonder filter doorgaan
                  </p>
                ) : (
                  <div className="space-y-2">
                    {endpointDefinition.parameters
                      .filter((param) => selectedParameters.has(param.id))
                      .map((param) => (
                        <div key={param.id} className="corner-squircle flex items-center gap-3 p-3 border rounded-[1.2rem]">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium">{param.id}</span>
                              <Badge color={getRequiredBadgeColor(param.required)}>
                                {getRequiredLabel(param.required)}
                              </Badge>
                            </div>
                            {param.description && (
                              <p className="text-xs text-gray-600">{param.description}</p>
                            )}
                            {renderParamValueInput(
                              param,
                              parameters[param.id] || "",
                              (v) => handleParameterChange(param.id, v),
                              "mt-2"
                            )}
                          </div>
                          {param.required !== "V" && (
                            <button
                              onClick={() => handleParameterToggle(param.id, false)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <RiCloseLine className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          </>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t">
          <Button onClick={handleBack} disabled={currentStep === "endpoint"} plain>
            <RiArrowLeftSLine className="w-4 h-4 mr-1" />
            Terug
          </Button>
          <div className="flex gap-2">
            {currentStep !== "endpoint" && (
              <Button onClick={handleNext} color="primary" disabled={loading}>
                {currentStep === "filters" ? "Ophalen" : "Volgende"}
                <RiArrowRightSLine className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
        </>}
      </div>
    </div>
  );
};

export default DataLoadingWizard;
